import type { ReactNode } from 'react';
import { Alert, Group, Loader, Table, Text } from '@mantine/core';
import { IconAlertCircle, IconFileOff, type Icon } from '@tabler/icons-react';
import { EmptyState } from '../../../components/EmptyState';

export interface ReportColumn<T> {
  key: string;
  header: string;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
}

interface ReportTableProps<T> {
  columns: ReportColumn<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => string;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  /** Used as the EmptyState title when `rows` is empty. */
  emptyMessage?: string;
  /** Short hint line under `emptyMessage`, e.g. "Try widening the date range or
   * clearing a filter." Optional — most reports read fine with just the title. */
  emptyHint?: string;
  /** Icon shown above `emptyMessage`. @default IconFileOff */
  emptyIcon?: Icon;
  /** Rendered below the header row, above the body — e.g. a totals row. */
  footer?: ReactNode;
}

/** Generic column-driven table shared by every report screen (goal text: "A shared
 * report-table + filter-bar component ... keeps the 12 consistent"). Handles the
 * loading/error/empty states once so each of the 12 report screens only supplies
 * its own column definitions + row shape. */
export function ReportTable<T>({
  columns,
  rows,
  getRowKey,
  isLoading,
  isError,
  errorMessage,
  emptyMessage,
  emptyHint,
  emptyIcon: EmptyIcon = IconFileOff,
  footer,
}: ReportTableProps<T>) {
  if (isLoading) {
    return (
      <Group>
        <Loader size="sm" />
        <Text c="dimmed">Loading report…</Text>
      </Group>
    );
  }

  if (isError) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        {errorMessage ?? 'Failed to load this report.'}
      </Alert>
    );
  }

  if (rows.length === 0) {
    return <EmptyState icon={EmptyIcon} title={emptyMessage ?? 'No data for the selected filters'} hint={emptyHint} />;
  }

  return (
    <Table.ScrollContainer minWidth={640}>
      <Table verticalSpacing="xs" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {columns.map((col) => (
              <Table.Th key={col.key} style={{ textAlign: col.align ?? 'left' }}>
                {col.header}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, index) => (
            <Table.Tr key={getRowKey(row, index)}>
              {columns.map((col) => (
                <Table.Td key={col.key} style={{ textAlign: col.align ?? 'left' }}>
                  {col.render(row)}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
        {footer}
      </Table>
    </Table.ScrollContainer>
  );
}
